import { Fragment, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';

/**
 * Generic slide-over from either edge, now with:
 * ✔ Scroll lock
 * ✔ Full-height mobile support
 * ✔ Pure JS swipe-to-close gesture
 */
export default function SlideOver({ open, onClose, side = 'left', children }) {
  const translate = side === 'left' ? '-translate-x-full' : 'translate-x-full';
  const panelRef = useRef(null);

  /* ─────────────────────────────────────────────
   *  PATCH 1 — Scroll Lock
     Prevent body scroll while drawer is open.
   * ───────────────────────────────────────────── */
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  /* ─────────────────────────────────────────────
   *  PATCH 3 — Swipe-to-close Gesture (Option A)
     Simple vanilla JS swipe detection.
   * ───────────────────────────────────────────── */
  useEffect(() => {
    if (!open || !panelRef.current) return;

    const panel = panelRef.current;
    let startX = 0;
    let currentX = 0;
    let dragging = false;

    const handleTouchStart = (e) => {
      dragging = true;
      startX = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
      if (!dragging) return;

      currentX = e.touches[0].clientX;
      const delta = currentX - startX;

      // Only allow swiping outward (direction depends on side)
      const shouldMove =
        (side === 'left' && delta < 0) ||
        (side === 'right' && delta > 0);

      if (!shouldMove) return;

      // Move drawer with finger
      panel.style.transform =
        `translateX(${delta}px)`;
    };

    const handleTouchEnd = () => {
      dragging = false;
      const delta = currentX - startX;

      // Threshold for closing (60px swipe)
      const threshold = 60;
      const shouldClose =
        (side === 'left' && delta < -threshold) ||
        (side === 'right' && delta > threshold);

      // Reset transform state
      panel.style.transform = '';

      if (shouldClose) {
        onClose();
      }
    };

    panel.addEventListener('touchstart', handleTouchStart);
    panel.addEventListener('touchmove', handleTouchMove);
    panel.addEventListener('touchend', handleTouchEnd);

    return () => {
      panel.removeEventListener('touchstart', handleTouchStart);
      panel.removeEventListener('touchmove', handleTouchMove);
      panel.removeEventListener('touchend', handleTouchEnd);
    };
  }, [open, side, onClose]);


  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-40 flex" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-50"
          leave="transition-opacity ease-in duration-200"
          leaveFrom="opacity-50"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-black bg-opacity-30" />
        </Transition.Child>

        {/* Panel */}
        <Transition.Child
          as={Fragment}
          enter="transform transition ease-in-out duration-300"
          enterFrom={translate}
          enterTo="translate-x-0"
          leave="transform transition ease-in-out duration-300"
          leaveFrom="translate-x-0"
          leaveTo={translate}
        >
          <div
            ref={panelRef}
            className={`
              relative
              h-full max-h-screen
              w-[85vw] max-w-[320px]
              bg-very-light-gray shadow-xl ring-1 ring-black/5
              ${side === 'right' ? 'ml-auto' : ''}
              flex flex-col
            `}
          >
            {children}
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}